import { type ReactNode } from 'react';

import { useInView } from 'react-intersection-observer';

import { styled } from '@prose-ui/legacy';

import { Image, type StaticImageData } from 'Components/Image';
import { Video } from 'Components/Video';

import pauseButton from 'assets/images/homeRevamp/pause-button.svg';
import playButton from 'assets/images/homeRevamp/play-button.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

type DeviceTypes = 'mobile' | 'tablet' | 'desktop';
type Devices<T> = Record<DeviceTypes, T>;

const Root = styled.div`
  position: relative;
  width: 100%;
`;

const Children = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
`;

const Overlay = styled.button`
  position: absolute;
  z-index: 2;
  right: 20px;
  bottom: 20px;

  border: none;

  background: none;
`;

type BackgroundVideoProps = {
  autoplayDelay?: number;
  children?: ReactNode;
  dataTestId?: string;
  dataFrom?: string;
  fitCover?: boolean;
  forcePause?: boolean;
  imageAlt: string;
  isLegacyBreakpoints?: boolean;
  lazyLoad?: boolean;
  onPause?: () => void;
  onPlay?: () => void;
  posterImages: Devices<StaticImageData> | StaticImageData;
  trackingText?: string;
  videoSources: Devices<string> | string;
};

export const BackgroundVideo = ({
  autoplayDelay = 0,
  children,
  dataFrom,
  dataTestId,
  fitCover = false,
  forcePause = false,
  imageAlt,
  isLegacyBreakpoints = true,
  lazyLoad = false,
  onPause,
  onPlay,
  posterImages,
  trackingText = '',
  videoSources,
}: BackgroundVideoProps) => {
  const { isTablet, isDesktop } = useResponsiveVariant({ legacy: isLegacyBreakpoints });
  const deviceSize: DeviceTypes = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile';

  const videoSource = typeof videoSources === 'string' ? videoSources : videoSources[deviceSize];
  const posterImageSource = 'src' in posterImages ? posterImages : posterImages[deviceSize];

  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  return (
    <Root ref={ref} data-testid={dataTestId}>
      <Video
        autoplay={!lazyLoad || inView}
        autoplayDelay={autoplayDelay}
        dataFrom={dataFrom}
        fitCover={fitCover}
        forcePause={forcePause}
        fullHeight
        fullWidth
        loop
        muted
        onPause={onPause}
        onPlay={onPlay}
        overlay={{
          playing: (
            <Overlay data-click="pause" data-from={dataFrom} tabIndex={0}>
              <Image
                alt="pause video"
                height={30}
                src={pauseButton}
                style={{ display: 'block' }}
                width={30}
              />
            </Overlay>
          ),
          paused: (
            <Overlay data-click="play" data-from={dataFrom} tabIndex={0}>
              <Image
                alt="play video"
                height={30}
                src={playButton}
                style={{ display: 'block' }}
                width={30}
              />
            </Overlay>
          ),
        }}
        posterImage={posterImageSource}
        posterImageAlt={imageAlt}
        trackingText={trackingText}
        videoSource={videoSource}
      />
      {children && <Children>{children}</Children>}
    </Root>
  );
};
